import {
  Heart,
  Target,
  Sparkles,
  CheckCheck,
  TrendingUp,
  User,
  Users,
  Crosshair,
  Compass,
  Brain,
  Map,
  Flame,
  LayoutDashboard,
  ClipboardList,
  type LucideIcon,
} from "lucide-react";

export type Persona = "individual" | "team" | "coach";

export interface FlowStep {
  icon: LucideIcon;
  label: string;
}

export interface PainPoint {
  emoji: string;
  title: string;
  description: string;
}

export interface FeatureCardData {
  icon: LucideIcon;
  iconGradientFrom: string;
  iconGradientTo: string;
  iconColor: string;
  tag: string;
  tagColor: string;
  heading: string;
  firstParagraph: string;
  hoverBorderColor: string;
  hoverShadowColor: string;
  expandedContent: string[];
}

export interface PersonaContent {
  // Switcher
  label: string;
  icon: LucideIcon;

  // Hero
  headline: string;
  headlineGradient: string;
  subheadlines: string[];
  ctaText: string;
  flowSteps: FlowStep[];

  // Pain Points
  sectionHeader: string;
  sectionTransition: string;
  painPoints: PainPoint[];

  // Features
  features: FeatureCardData[];
}

// ─── Shared Feature Cards ────────────────────────────────────────────────────

const valuesDiscovery: FeatureCardData = {
  icon: Compass,
  iconGradientFrom: "from-primary/20",
  iconGradientTo: "to-accent/20",
  iconColor: "text-primary",
  tag: "The Foundation",
  tagColor: "bg-primary/10 text-primary",
  heading: "Four Ways to Discover Your Core Values",
  firstParagraph:
    "Everything starts here. Not a personality quiz. Not generic categories. A research-backed exploration of what guides your career decisions.",
  hoverBorderColor: "hover:border-primary/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-primary/5",
  expandedContent: [
    "Choose your method: AI Conversation, Values Card Sort, Reflection Prompts, or Quick Assessment.",
    "Takes 5-15 minutes. Unlocks your entire personalized journey.",
    "Your values become the lens through which you evaluate every goal, opportunity, and next step.",
  ],
};

const aiCareerCoach: FeatureCardData = {
  icon: Brain,
  iconGradientFrom: "from-accent/20",
  iconGradientTo: "to-primary/20",
  iconColor: "text-accent",
  tag: "Context-Aware, Not Generic",
  tagColor: "bg-accent/10 text-accent",
  heading: "AI Coaching That Knows Your Role, Values, and Goals",
  firstParagraph:
    "Not ChatGPT with a 'career advisor' prompt. A coach grounded in your current role, target role, values, active goals, and key skills.",
  hoverBorderColor: "hover:border-accent/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-accent/5",
  expandedContent: [
    "Three modes: Skill Gap Analysis, Goal Coaching, and General Coaching.",
    "Every response is personalized to you — not generic advice scraped from the internet.",
    "Remembers your coaching history for continuity across sessions.",
  ],
};

const devPlanGenerator: FeatureCardData = {
  icon: Map,
  iconGradientFrom: "from-warning/20",
  iconGradientTo: "to-accent/20",
  iconColor: "text-warning",
  tag: "AI-Powered Roadmap",
  tagColor: "bg-warning/10 text-warning",
  heading: "Get a 6-Month Personalized Roadmap in 3 Clicks",
  firstParagraph:
    "Input your values, goals, current role, target role, and skills. AI creates a living, trackable roadmap — not a static PDF.",
  hoverBorderColor: "hover:border-warning/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-warning/5",
  expandedContent: [
    "AI analyzes skill gaps, maps goals to the 3E Framework, and creates milestone progression.",
    "Visual progress dashboard with milestone completion tracking.",
    "Updates dynamically as you complete goals.",
  ],
};

const threeEFramework: FeatureCardData = {
  icon: Target,
  iconGradientFrom: "from-success/20",
  iconGradientTo: "to-primary/20",
  iconColor: "text-success",
  tag: "Proven Methodology",
  tagColor: "bg-success/10 text-success",
  heading: "Structure Your Growth: Education, Exposure, Experience",
  firstParagraph:
    "The 3E Framework ensures balanced development. Education alone is theory. Exposure alone is dabbling. Experience alone is guessing. All three together = compounding growth.",
  hoverBorderColor: "hover:border-success/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-success/5",
  expandedContent: [
    "Education (Learn It): courses, books, workshops, certifications.",
    "Exposure (Practice It): shadowing, side projects, presentations.",
    "Experience (Own It): lead projects, manage teams, ship products.",
  ],
};

const habitTracking: FeatureCardData = {
  icon: Flame,
  iconGradientFrom: "from-orange-500/20",
  iconGradientTo: "to-warning/20",
  iconColor: "text-orange-500",
  tag: "Daily Momentum",
  tagColor: "bg-orange-500/10 text-orange-500",
  heading: "Build Habits That Compound Into Career Transformation",
  firstParagraph:
    "Goals without daily habits are wishes. Create habits, track your streak, and let progress tracking keep you accountable.",
  hoverBorderColor: "hover:border-orange-500/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-orange-500/5",
  expandedContent: [
    "Link habits directly to goals. Each completion moves the progress bar.",
    "Streak tracking with visual momentum indicators.",
    "Small actions, repeated daily, become undeniable career progress.",
  ],
};

const mentorshipNetwork: FeatureCardData = {
  icon: Users,
  iconGradientFrom: "from-pink-500/20",
  iconGradientTo: "to-primary/20",
  iconColor: "text-pink-500",
  tag: "Two-Way Community",
  tagColor: "bg-pink-500/10 text-pink-500",
  heading: "Find Mentors. Become a Mentor. Accelerate Together.",
  firstParagraph:
    "Not a cold LinkedIn DM marketplace. A structured community with match scores based on your values and aspirations.",
  hoverBorderColor: "hover:border-pink-500/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-pink-500/5",
  expandedContent: [
    "Browse mentor profiles filtered by role, industry, and company.",
    "AI-powered match scores based on your values and aspirations.",
    "Two-way mentorship: seek guidance and give back to the community.",
  ],
};

// ─── Persona-Specific Feature Cards ──────────────────────────────────────────

const teamDashboard: FeatureCardData = {
  icon: LayoutDashboard,
  iconGradientFrom: "from-blue-500/20",
  iconGradientTo: "to-primary/20",
  iconColor: "text-blue-500",
  tag: "Team Visibility",
  tagColor: "bg-blue-500/10 text-blue-500",
  heading: "Team Dashboard & Analytics",
  firstParagraph:
    "See who's growing, who's stuck, and where to invest. Real-time engagement metrics, goal completion rates, and values alignment across your team.",
  hoverBorderColor: "hover:border-blue-500/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-blue-500/5",
  expandedContent: [
    "Seat management, role assignments, and team-wide progress tracking.",
    "Engagement metrics that show actual development activity, not just logins.",
    "Identify high-performers and team members who need support.",
  ],
};

const coachingDashboard: FeatureCardData = {
  icon: ClipboardList,
  iconGradientFrom: "from-emerald-500/20",
  iconGradientTo: "to-accent/20",
  iconColor: "text-emerald-500",
  tag: "Client Management",
  tagColor: "bg-emerald-500/10 text-emerald-500",
  heading: "Coaching Dashboard",
  firstParagraph:
    "See every client's values, goals, habits, and progress in one place. Know exactly where they are before every session.",
  hoverBorderColor: "hover:border-emerald-500/30",
  hoverShadowColor: "hover:shadow-lg hover:shadow-emerald-500/5",
  expandedContent: [
    "Client list with progress summaries and engagement indicators.",
    "Private coaching notes that persist across sessions.",
    "Daily visibility into client habits and goal completion.",
  ],
};

// ─── Persona Content ─────────────────────────────────────────────────────────

export const personaContent: Record<Persona, PersonaContent> = {
  individual: {
    label: "Individuals",
    icon: User,
    headline: "Stop guessing what to learn next.",
    headlineGradient:
      "Get a personalized roadmap from where you are to where you want to be.",
    subheadlines: [
      "Start with a 5-minute values assessment and get an AI-generated roadmap.",
      "Build daily habits that compound into career momentum.",
      "From IC3 to Senior, Associate to Partner — clear, systematic, grounded in your values.",
    ],
    ctaText: "Get Started Free",
    flowSteps: [
      { icon: Heart, label: "5-min values" },
      { icon: Target, label: "Goals" },
      { icon: Sparkles, label: "AI Plan" },
      { icon: CheckCheck, label: "Daily Habits" },
      { icon: TrendingUp, label: "Progress" },
    ],
    sectionHeader:
      'You\'re taking courses. Watching YouTube videos. Reading leadership books. <muted>Still stuck.</muted>',
    sectionTransition: "MomentumEQ gives you the system.",
    painPoints: [
      {
        emoji: "\u{1F630}",
        title: "Unclear Direction",
        description:
          "You don't know what to learn next. Every \"Top 10 Skills\" article conflicts. You're collecting certificates but not progressing.",
      },
      {
        emoji: "\u{23F0}",
        title: "Random Efforts",
        description:
          "You learn React one week, leadership the next, then cloud architecture. Nothing builds on anything. No compounding.",
      },
      {
        emoji: "\u{1F937}",
        title: "No Accountability",
        description:
          "No plan, no tracking, no feedback loop. You hope someone notices your growth. They don't.",
      },
    ],
    features: [
      valuesDiscovery,
      aiCareerCoach,
      devPlanGenerator,
      threeEFramework,
      habitTracking,
      mentorshipNetwork,
    ],
  },

  team: {
    label: "Teams",
    icon: Users,
    headline: "Give every team member a growth path",
    headlineGradient: "they'll actually follow.",
    subheadlines: [
      "Personalized development at scale — not one-size-fits-all training.",
      "Real-time visibility into who's growing, who's stuck, and where to invest.",
      "Values-aligned development that drives engagement, not just completion rates.",
    ],
    ctaText: "Request a Team Pilot",
    flowSteps: [
      { icon: Users, label: "Onboard team" },
      { icon: Heart, label: "Values" },
      { icon: Target, label: "Goals" },
      { icon: Sparkles, label: "AI Plans" },
      { icon: LayoutDashboard, label: "Dashboard" },
    ],
    sectionHeader:
      'You\'re investing in L&D. Running workshops. Buying course licenses. <muted>Nobody finishes them.</muted>',
    sectionTransition:
      "MomentumEQ gives every team member a personal growth system.",
    painPoints: [
      {
        emoji: "\u{1F441}\u{FE0F}",
        title: "No Visibility",
        description:
          "You can't see who's actually growing vs. who's just logging in. Engagement metrics are vanity metrics.",
      },
      {
        emoji: "\u{1F4E6}",
        title: "Generic L&D",
        description:
          "Same training for everyone. Low completion rates. No connection to what individuals actually need for their career path.",
      },
      {
        emoji: "\u{1F4CA}",
        title: "No Measurement",
        description:
          "You can't tie development spending to outcomes. No way to measure if L&D investment is actually driving growth.",
      },
    ],
    features: [
      teamDashboard,
      devPlanGenerator,
      threeEFramework,
      habitTracking,
      valuesDiscovery,
      aiCareerCoach,
    ],
  },

  coach: {
    label: "Coaches",
    icon: Crosshair,
    headline: "Keep clients engaged between sessions.",
    headlineGradient: "See their progress in real time.",
    subheadlines: [
      "A client dashboard that shows values, goals, habits, and progress — all in one place.",
      "Daily visibility into what your clients are actually doing between sessions.",
      "Coaching notes, engagement tracking, and session prep — no more spreadsheets.",
    ],
    ctaText: "Apply for Coach Access",
    flowSteps: [
      { icon: Users, label: "Invite clients" },
      { icon: Heart, label: "Values" },
      { icon: Target, label: "Goals" },
      { icon: Flame, label: "Daily Habits" },
      { icon: ClipboardList, label: "Dashboard" },
    ],
    sectionHeader:
      'You have great sessions. Clients leave motivated. <muted>Then nothing happens until next time.</muted>',
    sectionTransition:
      "MomentumEQ keeps clients engaged between every session.",
    painPoints: [
      {
        emoji: "\u{1F4AD}",
        title: "Session Gap",
        description:
          "Clients disengage between sessions. Momentum fades. You spend the first 15 minutes of each session catching up.",
      },
      {
        emoji: "\u{1F648}",
        title: "Blind Spots",
        description:
          "No visibility into daily behavior. You rely on self-reported progress, which is unreliable and incomplete.",
      },
      {
        emoji: "\u{1F4CB}",
        title: "Manual Tracking",
        description:
          "Spreadsheets, scattered notes, and memory. No single place to see a client's full development picture.",
      },
    ],
    features: [
      coachingDashboard,
      habitTracking,
      valuesDiscovery,
      devPlanGenerator,
      aiCareerCoach,
      threeEFramework,
    ],
  },
};

export const PERSONA_HASH_MAP: Record<string, Persona> = {
  individuals: "individual",
  teams: "team",
  coaches: "coach",
};

export const PERSONA_TO_HASH: Record<Persona, string> = {
  individual: "individuals",
  team: "teams",
  coach: "coaches",
};
